export function joinClasses(...parts) {
  return parts.filter(Boolean).join(' ');
}

export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
}

export function includesValue(value, optionValue) {
  return asArray(value).includes(optionValue);
}

export function toggleArrayValue(value, optionValue) {
  const list = asArray(value);
  return list.includes(optionValue)
    ? list.filter((item) => item !== optionValue)
    : [...list, optionValue];
}

export function getVisibleItems(items = [], limit = 4, expanded = false) {
  if (expanded || items.length <= limit) {
    return { visibleItems: items, hiddenCount: 0, hasOverflow: false };
  }
  const visibleItems = items.slice(0, limit);
  return {
    visibleItems,
    hiddenCount: Math.max(items.length - visibleItems.length, 0),
    hasOverflow: true,
  };
}

export function selectedOptionsFirst(options = [], value = []) {
  const selected = new Set(asArray(value));
  return [...options].sort((a, b) => {
    const aSelected = selected.has(a.value);
    const bSelected = selected.has(b.value);
    if (aSelected === bSelected) return 0;
    return aSelected ? -1 : 1;
  });
}

const sectionStrategies = {
  'numeric-range': {
    activeCount: (s) => Number(Boolean(s.value?.min || s.value?.max)),
    clear: (s) => { s.onChange?.({}); },
  },
  'date-range': {
    activeCount: (s) => Number(Boolean(s.value?.from || s.value?.to)),
    clear: (s) => { s.onChange?.({}); },
  },
  'ai-insights': {
    activeCount: (s) => {
      const tags = asArray(s.value?.tags);
      const allValue = s.allValue ?? 'any';
      const alertLevel = s.value?.alertLevel;
      return tags.length + Number(Boolean(alertLevel && alertLevel !== allValue));
    },
    clear: (s) => { s.onChange?.({ tags: [], alertLevel: s.allValue ?? 'any' }); },
  },
  'single-select': {
    activeCount: (s) => (!s.value || s.value === s.allValue ? 0 : 1),
    clear: (s) => { s.onChange?.(s.allValue ?? ''); },
  },
  'date-presets': {
    activeCount: (s) => (!s.value || s.value === s.allValue ? 0 : 1),
    clear: (s) => { s.onChange?.(s.allValue ?? ''); },
  },
  title: {
    activeCount: (s) => (!s.value || s.value === s.allValue ? 0 : 1),
    clear: (s) => { s.onChange?.(s.allValue ?? ''); },
  },
  sort: {
    activeCount: (s) => (s.value ? 1 : 0),
    clear: (s) => { s.onChange?.(s.options?.[0]?.value ?? ''); },
  },
  'chip-select': {
    activeCount: (s) => (s.multiple === false ? (s.value ? 1 : 0) : asArray(s.value).length),
    clear: (s) => { s.onChange?.(s.multiple === false ? '' : []); },
  },
  assignment: {
    activeCount: (s) => asArray(s.value).length,
    clear: (s) => { s.onChange?.([]); },
  },
};

const defaultStrategy = {
  activeCount: (s) => asArray(s.value).length,
  clear: (s) => { s.onChange?.([]); },
};

function getStrategy(type) {
  return sectionStrategies[type] ?? defaultStrategy;
}

export function getSectionActiveCount(section) {
  if (!section) return 0;
  return getStrategy(section.type).activeCount(section);
}

export function sectionHasActiveFilter(section) {
  return getSectionActiveCount(section) > 0;
}

export function clearSection(section) {
  section?.onClear?.(section.id);
  if (!section?.onChange) return;
  getStrategy(section.type).clear(section);
}

export function clearAllSections(sections = []) {
  sections.forEach(clearSection);
}

export function hasInvalidNumericRange(section) {
  if (section?.type !== 'numeric-range') return false;
  const min = section.value?.min;
  const max = section.value?.max;
  if (min === '' || min == null || max === '' || max == null) return false;
  const minNumber = Number(min);
  const maxNumber = Number(max);
  if (Number.isNaN(minNumber) || Number.isNaN(maxNumber)) return true;
  return minNumber > maxNumber;
}

export function hasInvalidSections(sections = []) {
  return sections.some(hasInvalidNumericRange);
}
